import { Skeleton } from 'antd'
import { IoChevronForward } from 'react-icons/io5'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { FormSection } from '@/Ship/Components/Form/FormSection/FormSection'
import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'

import { RELATED_RECORDS_LIMIT, type RelatedRecord, type RelatedRecordsState } from '../../Hooks/useRelatedRecords'

import styles from './RelatedRecords.module.css'

import type { IResourceConfig } from '@/Core/Contracts/UI'
import type { CSSProperties } from 'react'
import type { IconType } from 'react-icons'

/** Скільки плиток-скелетонів показати, поки записи вантажаться */
const SKELETON_TILES = 3

interface RelatedRecordsProps {
    /** Заголовок секції, напр. «Регіони» */
    title: string
    state: RelatedRecordsState
    /** Ресурс пов'язаних записів: клік по плитці відкриває його картку */
    resource: IResourceConfig
    /** Іконка активного запису і неактивного (вона ж — у порожньому стані) */
    icon: IconType
    inactiveIcon: IconType
    /** Текст порожнього стану, напр. «У кластері ще немає регіонів» */
    emptyText: string
}

/** «3 активні · 1 неактивний» під заголовком; без неактивних — лише перша частина */
const summaryText = (items: RelatedRecord[]): string => {
    const inactive = items.filter((record) => !record.isActive).length
    const active = items.length - inactive
    const parts = [`${active} ${pluralizeUk(active, ['активний', 'активні', 'активних'])}`]
    if (inactive > 0) parts.push(`${inactive} ${pluralizeUk(inactive, ['неактивний', 'неактивні', 'неактивних'])}`)
    return parts.join(' · ')
}

interface TileProps extends Pick<RelatedRecordsProps, 'resource' | 'icon' | 'inactiveIcon'> {
    record: RelatedRecord
    index: number
}

const Tile = ({ record, index, resource, icon, inactiveIcon }: TileProps) => {
    const Icon = record.isActive ? icon : inactiveIcon
    const meta = [record.code, record.externalCode].filter(Boolean).join(' · ')
    return (
        <li className={cx(styles, 'item')} style={{ '--tile-index': index } as CSSProperties}>
            <button
                type="button"
                className={`${cx(styles, 'tile')} ${record.isActive ? '' : cx(styles, 'tileInactive')}`}
                onClick={() => OpenResourceCardTask({ resource, primaryFieldValue: record.code, mode: 'view' })}
                title={record.isActive ? record.title : `${record.title} (неактивний)`}
            >
                <span className={cx(styles, 'tileIcon')} aria-hidden="true">
                    <Icon />
                </span>
                <span className={cx(styles, 'tileMain')}>
                    <span className={cx(styles, 'tileTitle')}>{record.title}</span>
                    <span className={cx(styles, 'tileMeta')}>
                        {!record.isActive && <span className={cx(styles, 'badge')}>Неактивний</span>}
                        {meta}
                    </span>
                </span>
                <IoChevronForward className={cx(styles, 'chevron')} aria-hidden="true" />
            </button>
        </li>
    )
}

const Body = ({ title, state, resource, icon, inactiveIcon, emptyText }: RelatedRecordsProps) => {
    if (state.status === 'loading') {
        return (
            <div className={cx(styles, 'grid')} role="status" aria-label={`${title}: завантаження`}>
                {Array.from({ length: SKELETON_TILES }, (_, i) => (
                    <div key={i} className={cx(styles, 'skeletonTile')}>
                        <Skeleton.Avatar active size={36} />
                        <Skeleton active title={false} paragraph={{ rows: 2, width: ['70%', '40%'] }} />
                    </div>
                ))}
            </div>
        )
    }

    if (state.status === 'error') {
        return <p className={cx(styles, 'message')}>Не вдалося завантажити список</p>
    }

    if (state.items.length === 0) {
        const EmptyIcon = inactiveIcon
        return (
            <div className={cx(styles, 'empty')}>
                <span className={cx(styles, 'emptyIcon')} aria-hidden="true">
                    <EmptyIcon />
                </span>
                <span>{emptyText}</span>
            </div>
        )
    }

    return (
        <>
            <ul className={cx(styles, 'grid')}>
                {state.items.map((record, index) => (
                    <Tile
                        key={record.code}
                        record={record}
                        index={index}
                        resource={resource}
                        icon={icon}
                        inactiveIcon={inactiveIcon}
                    />
                ))}
            </ul>
            {state.total > state.items.length && (
                <p className={cx(styles, 'message')}>
                    Показано перші {RELATED_RECORDS_LIMIT} з {state.total}
                </p>
            )}
        </>
    )
}

/**
 * Секція пов'язаних записів у картці довідника (регіони кластера, об'єкти регіону):
 * заголовок з лічильником і підсумком активності, сітка плиток (іконка, назва,
 * код · ERP-код) у стилі огляду довідників; клік по плитці відкриває картку запису.
 */
export const RelatedRecords = (props: RelatedRecordsProps) => {
    const { title, state } = props
    const done = state.status === 'done'
    const heading = (
        <span className={cx(styles, 'header')}>
            <span>{title}</span>
            {done && <span className={cx(styles, 'counter')}>{state.total}</span>}
        </span>
    )

    return (
        <FormSection title={heading} hint={done && state.items.length > 0 ? summaryText(state.items) : undefined}>
            <Body {...props} />
        </FormSection>
    )
}
